import {UserPreferencesStore} from "../State";
import {
    MOBILE_KEYBOARD_TOGGLE_TRANSITION_TIME,
    WALLET_COLLAPSED_VISIBLE_BALANCE_WIDTH,
    WALLET_COLLAPSED_WIDTH,
    WIDGET_ANCHOR_OFFSET, WIDGET_EXPANDED_DESKTOP_BOTTOM_OFFSET, WIDGET_MAX_HEIGHT,
    WIDGET_SCREEN_MIN_PADDING,
} from "./WidgetConstants";
import {BlinkGlobal, isKeyboardExpanded, isSmallScreen} from "../Utils";
import {CallThrottler} from "../../stem-core/src/base/Utils";
import {Dispatcher} from "../../stem-core/src/base/Dispatcher";
import {authService} from "../../client/connection/Services";
import {iFrameState} from "../services/IFrameState";
import {getWidgetRightPosition} from "../../blink-sdk/utils/Utils";
import {collapseIframe} from "./misc/WidgetUtils";
import {BasePanel} from "../panel/pages/panels/BasePanel";
import {Router} from "../../stem-core/src/ui/Router";
import {IFRAME_APP_TYPE, PANEL_TYPE, POPUP_GATE_TRANSITION_TIME} from "../../blink-sdk/Constants";
import {iFrameConnection} from "../services/IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";


function getWidgetExpandedBottom() {
    if (isSmallScreen()) {
        return 0;
    }

    const defaultExpandedBottom = WIDGET_EXPANDED_DESKTOP_BOTTOM_OFFSET + iFrameState.positionBottom;
    const widgetReservedHeight = WIDGET_MAX_HEIGHT + defaultExpandedBottom;

    if (widgetReservedHeight > document.documentElement.clientHeight) {
        if (WIDGET_EXPANDED_DESKTOP_BOTTOM_OFFSET + WIDGET_MAX_HEIGHT <= document.documentElement.clientHeight) {
            return WIDGET_EXPANDED_DESKTOP_BOTTOM_OFFSET;
        }
        return 0;
    }
    return defaultExpandedBottom;
}

export const addScrimListeners = (appType) => {
    const addClickListener = (element, callback) => {
        const clickEvents = ["touchstart", "touchend", "click", "mousedown"];
        for (const eventType of clickEvents) {
            element.addEventListener(eventType, callback);
        }
    }

    let lastEventTimestamp = -600;
    let clickDisabled = false;
    addClickListener(document.body, event => {
        event.stopPropagation();
        clickDisabled = true;
        setTimeout(() => {
            clickDisabled = false;
        });
    });
    addClickListener(document, event => {
        if (!clickDisabled && event.timeStamp - lastEventTimestamp >= 500) {
            if (appType === IFRAME_APP_TYPE.panel) {
                if (Router.Global.getActive() instanceof BasePanel && Router.getCurrentPath() !== PANEL_TYPE.empty && iFrameState.scrimClickCollapse) {
                    collapseIframe();
                }
            }
            if (appType === IFRAME_APP_TYPE.widget) {
                if (!iFrameState.activePanel) {
                    collapseIframe();
                }
            }
        }
        lastEventTimestamp = event.timeStamp;
    });
};

class WidgetAnimation {
    lastHeight = 0;
    lastHeightDelta = 0;
    collapsed = true;
    activeElementScrollTimeout = null;

    updateThrottler = new CallThrottler({throttle: 50});
    updateThrottled = this.updateThrottler.wrap(() => {
        this.handleAnimation();
    });

    constructor() {
        Dispatcher.Global.addListener("widgetAnimationUpdateHeightDelta", heightDelta => this.updateHeightDelta(heightDelta));
        Dispatcher.Global.addListener("widgetAnimationUpdateHeight", height => this.updateHeight(height));
    }

    setWidgetStyle(style) {
        BlinkGlobal.appInstance.animationStyleDispatcher.dispatch(style);
        iFrameConnection.sendToSDK(IFrameMessages.TRANSITION_CHANGE, {transitioning: true});
        setTimeout(() => {
            iFrameConnection.sendToSDK(IFrameMessages.TRANSITION_CHANGE, {transitioning: false});
        }, POPUP_GATE_TRANSITION_TIME);
    }

    initListeners() {
        // This is for the "showBalance" feature.
        UserPreferencesStore.addChangeListener(() => this.updateThrottled());

        let smallScreen = isSmallScreen();
        window.addEventListener("resize", () => {
            if (document.activeElement.tagName === "INPUT") {
                clearTimeout(this.activeElementScrollTimeout);
                this.activeElementScrollTimeout = setTimeout(() => document.activeElement.scrollIntoView({block: "center"}), MOBILE_KEYBOARD_TOGGLE_TRANSITION_TIME);
            }
            if (isSmallScreen() === smallScreen) {
                return;
            }
            smallScreen = isSmallScreen();
            this.updateThrottled();
        });

        authService.onAuthenticationChange(() => this.handleAnimation());
    }

    computeStyle(collapsed) {
        if (collapsed) {
            const preferences = UserPreferencesStore.getPreferences();
            const shouldShowBalance = preferences && preferences.alwaysShowBalance;
            const width = shouldShowBalance ? WALLET_COLLAPSED_VISIBLE_BALANCE_WIDTH : WALLET_COLLAPSED_WIDTH;

            return authService.isAuthenticated() ? {
                width: width + "px",
                borderRadius: "40px",
                right: getWidgetRightPosition() + "px",
                transform: "translate(0, 0)",
            } : {};
        }

        return authService.isAuthenticated() ? {
            width: "360px",
            borderRadius: "12px",
            ...(isSmallScreen() ? {
                right: "50%",
                transform: "translate(180px, 0)"
            } : {
                right: getWidgetRightPosition() + "px",
                transform: "translate(0, 0)"
            })
        } : {};
    }

    handleAnimation(collapsed=null) {
        if (collapsed == null && this.collapsed == null) {
            return;
        }
        if (collapsed == null) {
            collapsed = this.collapsed;
        }
        this.setWidgetStyle(this.computeStyle(collapsed));
        this.collapsed = collapsed;
    }

    setBodyHeight(height) {
        if (authService.isAuthenticated()) {
            this.setWidgetStyle({
                height: height + "px",
            });
        }
    }

    repositionWidget() {
        if (!authService.isAuthenticated()) {
            return;
        }
        setTimeout(() => {
            const windowHeight = window.innerHeight;
            const widgetHeight = this.lastHeight + this.lastHeightDelta;
            const halfEmptySpace = (windowHeight - widgetHeight) / 2;
            const {positionBottom, widgetVisible} = iFrameState;

            let bodyStyle;
            if (this.collapsed || isKeyboardExpanded() || !isSmallScreen()) {
                bodyStyle = this.collapsed ? {
                    maxHeight: widgetHeight + "px",
                    bottom: widgetVisible ? positionBottom + "px" : "",
                } : {
                    maxHeight: windowHeight + "px",
                    bottom: widgetVisible ? getWidgetExpandedBottom() + "px" : "",
                };
            } else if (halfEmptySpace >= WIDGET_ANCHOR_OFFSET) {
                bodyStyle = {
                    bottom: WIDGET_ANCHOR_OFFSET + "px",
                };
            } else if (halfEmptySpace >= WIDGET_SCREEN_MIN_PADDING) {
                bodyStyle = {
                    bottom: `calc(50% - ${widgetHeight / 2}px)`,
                };
            } else {
                bodyStyle = {
                    bottom: WIDGET_SCREEN_MIN_PADDING + "px",
                }
            }

            this.setWidgetStyle({
                ...bodyStyle,
                maxHeight: windowHeight - 2 * WIDGET_SCREEN_MIN_PADDING + "px",
            });
        });
    }

    updateHeight(height) {
        if (height === this.lastHeight) {
            return;
        }
        const newWidgetHeight = height + this.lastHeightDelta;
        this.lastHeight = height;
        this.setBodyHeight(newWidgetHeight);
        this.repositionWidget();
    }

    updateHeightDelta(heightDelta) {
        if (heightDelta === this.lastHeightDelta) {
            return;
        }
        const newWidgetHeight = this.lastHeight + heightDelta;
        this.lastHeightDelta = heightDelta;
        this.setBodyHeight(newWidgetHeight);
        this.repositionWidget();
    }
}

export const widgetAnimation = new WidgetAnimation();
